/**
 * Coach Live Training station focus — pick and stay on any station/subgroup.
 */

import { pickStationScopedExerciseIndex } from "./tableSeats";
import { buildPlanningLanes } from "./planningSubgroups";
import { pickCoachControlTargetIndex } from "./coachExerciseNavigation";

export const LIVE_TRAINING_TSG_STORAGE_KEY = "live_training_tsg_id";

export function persistLiveTrainingTsgId(tsgId) {
  try {
    if (tsgId) {
      sessionStorage.setItem(LIVE_TRAINING_TSG_STORAGE_KEY, String(tsgId));
    } else {
      sessionStorage.removeItem(LIVE_TRAINING_TSG_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function readLiveTrainingTsgId() {
  try {
    return sessionStorage.getItem(LIVE_TRAINING_TSG_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

export function stationLabel(station, t) {
  if (!station) return t?.("mainGroup") || "Main group";
  if (station.is_main) return t?.("mainGroup") || "Main group";
  return station.name || t?.("stations") || "Station";
}

/** True when the session has more than one station lane worth managing. */
export function hasMultipleLiveStations(stations = []) {
  return (stations || []).filter((s) => s?.tsg_id).length > 1;
}

export function buildLiveStations({ subgroups = [], exercises = [] } = {}) {
  return buildPlanningLanes({ subgroups, exercises });
}

export function resolveFocusedTsgId(stations = [], preferredTsgId = null) {
  const list = stations || [];
  if (!list.length) return null;
  if (
    preferredTsgId &&
    list.some((s) => String(s.tsg_id) === String(preferredTsgId))
  ) {
    return String(preferredTsgId);
  }
  const stored = readLiveTrainingTsgId();
  if (stored && list.some((s) => String(s.tsg_id) === String(stored))) {
    return String(stored);
  }
  const main = list.find((s) => s.is_main) || list[0];
  return main?.tsg_id != null ? String(main.tsg_id) : null;
}

export function exercisesForStation(exercises = [], tsgId = null) {
  const list = Array.isArray(exercises) ? exercises : [];
  if (!tsgId) return list;
  // Never fall back to another station's blocks — empty means this station
  // has no plan rows yet (UI should show empty / assign, not the main group).
  return list.filter((ex) => String(ex.tsg_id || "") === String(tsgId));
}

export function globalIndexForTeId(exercises = [], teId = null) {
  if (teId == null) return -1;
  return (exercises || []).findIndex((ex) => String(ex?.te_id) === String(teId));
}

/**
 * Pick coach focus index, optionally scoped to a station.
 * When tsgId is set, never jumps to another station's TE.
 */
export function pickCoachStationControlIndex(
  exercises,
  realtimeByTeId = {},
  tsgId = null
) {
  if (!tsgId) {
    return pickCoachControlTargetIndex(exercises, realtimeByTeId);
  }
  return pickStationScopedExerciseIndex(exercises, realtimeByTeId, tsgId);
}

/**
 * After a TE state change: keep coach on the same station.
 * Only retarget when the event TE belongs to the focused station (or focus unset).
 */
export function nextCoachIndexAfterStateChange({
  exercises = [],
  realtimeByTeId = {},
  prevIndex = 0,
  changedTeId = null,
  focusedTsgId = null,
} = {}) {
  const list = exercises || [];
  if (!list.length) return prevIndex;

  if (focusedTsgId) {
    const changed = list.find((ex) => String(ex?.te_id) === String(changedTeId));
    if (changed && String(changed.tsg_id || "") !== String(focusedTsgId)) {
      return prevIndex;
    }
    const next = pickCoachStationControlIndex(
      list,
      realtimeByTeId,
      focusedTsgId
    );
    return next >= 0 ? next : prevIndex;
  }

  const next = pickCoachStationControlIndex(list, realtimeByTeId, null);
  return next >= 0 ? next : prevIndex;
}
